import React, { useContext, useEffect, useState } from 'react';
import { Box, IconButton, Paper, SvgIcon } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { customSizes, palette, spaces } from '../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../utils/getMuiIcon';
import { StoreType } from '../../../reducers';
import { selectMindItem } from '../../../reducers/mindItems.selectors';
import { ReaderContext } from '../../Reader/ReaderContext';
import ReaderDetails from './ReaderDetails';

export const CONTENT_REAPPEAR_DELAY = 150;

interface ReaderDetailsWrapperProps {
  isPublicVideoPlayer?: boolean;
}
const ReaderDetailsWrapper: React.FC<ReaderDetailsWrapperProps> = ({ isPublicVideoPlayer = false }) => {
  const {
    mindItemId,
    isReaderDetailsCollapsed,
    setReaderDetailsCollapsed,
    fullScreenMode,
  } = useContext(ReaderContext);

  const { t } = useTranslation();
  const mindItem = useSelector((state: StoreType) => selectMindItem(state, mindItemId));
  const [buttonDimensions, setButtonDimensions] = useState({ width: 0, height: 0 });
  const [isContentHidden, setContentHidden] = useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (buttonRef.current) {
      const { width, height } = buttonRef.current.getBoundingClientRect();
      setButtonDimensions({ width, height });
    }
  }, [buttonRef.current]);

  const truncate = () => {
    if (setReaderDetailsCollapsed) {
      setReaderDetailsCollapsed(true);
    }
    setContentHidden(true);
  };

  const expand = () => {
    if (setReaderDetailsCollapsed) {
      setReaderDetailsCollapsed(false);
    }
    setTimeout(() => setContentHidden(false), CONTENT_REAPPEAR_DELAY);
  };

  if (!mindItem) return null;

  const expandedStyle = {
    height: '100%',
    width: customSizes.readerDetails.width,
  };

  const renderExpendButton = () => {
    const isVideo = mindItem?.fileType === 'video';
    const expandButtonLabel = isPublicVideoPlayer || isVideo 
      ? t('Show video details') 
      : t('Show product details');

    return (
      <Box
        sx={{
          visibility: 'visible',
          position: 'relative',
        }}
      >
        <IconButton
          ref={buttonRef}
          onClick={expand}
          size='large'
          sx={{
            color: palette.text.primary,
          }}
          data-testid='product-details__expand-button'
          aria-label={expandButtonLabel}
        >
          <SvgIcon component={getMuiIcon('clipboardText')} aria-hidden='true' />
        </IconButton>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        paddingY: customSizes.readerPage.paddingY,
        ...(fullScreenMode ? {
          display: 'none',
        } : {}),
      }}
    >
      <Paper
        elevation={2}
        sx={{
          borderRadius: spaces.sm,
          color: palette.text.primary,
          display: 'flex',
          gap: spaces.xxs,
          flexDirection: 'column',
          ...(isReaderDetailsCollapsed ? { ...buttonDimensions } : expandedStyle),
          transition: 'width 0.25s, height 0.25s',
          transitionTimingFunction: 'cubic-bezier(0.1, 0.57, 0.1, 1)',
          paddingBottom: spaces.sm,
        }}
      >
        {
          isReaderDetailsCollapsed ?
            renderExpendButton() :
            <ReaderDetails
              topRightAction={{
                icon: <SvgIcon component={getMuiIcon('arrowLeft')} aria-hidden='true' />,
                onClick: truncate,
                buttonSx: {
                  marginRight: `-${spaces.sm}`
                }
              }}
              isContentHidden={isContentHidden}
              mindItemId={mindItemId}
              isPublicVideoPlayer={isPublicVideoPlayer}
            />
        }
      </Paper>
    </Box>
  );
};

export default ReaderDetailsWrapper;
